import { PriceCurve, PriceRole } from '@digital-twins/shared-types'

import { DECK_CHART_SERIES_COLORS } from '../../../foundations/chart-colors'
import { priceDecimals, referencePriceDecimals } from '../../../foundations/price-decimals'

/**
 * PriceCurveChart — the Van Westendorp PSM curves (D5, D15).
 *
 * Ported from the report's `PriceCurveCard` (`SummaryTab.tsx`) rather than
 * imported: this is now the deck-ui payload-fed view for the
 * `PriceCurvePayload` summary shape, and `SummaryTab.tsx` is Step 5's file.
 * PSM only -- four series, two crossing points, one optimal marker, one
 * legend. No Gabor-Granger / `demand_curve` chart here (D15): the schema
 * accepts the shape, no producer emits one, so there is nothing to draw yet.
 *
 * Labels are passed in, never hardcoded English or an i18n import: deck-ui
 * has no translation runtime of its own (see `RankingChartLabels` for the
 * same pattern), so a caller in the app supplies translated strings.
 *
 * Additive contract (D5b, pricing-psm-deck-storytelling plan Step 1 + 2). A
 * curve stored before `reported` existed renders exactly as it always did
 * (legacy branch below): the four series, the exact crossings as the lead
 * numbers, no band, no endpoint labels, no spend marker. Once `curve.reported`
 * is present, geometry stays exact -- the drawn band edges and the optimal
 * marker sit on `pointOfMarginalCheapness` / `pointOfMarginalExpensiveness` /
 * `optimalPricePoint`, never on a rounded number -- and only TEXT (the lead
 * figure, the endpoint labels, the optimal-point row) reads `reported` (D1,
 * "geometry is exact, labels are rounded").
 */
export interface PriceCurveChartLabels {
  priceSensitivity: string
  optimalPricePoint: string
  acceptableRange: string
  /** Joins the two ends of the acceptable range ("10 to 20"). */
  rangeSeparator: string
  noCurrency: string
  /** "Lower acceptable price" -- printed under the low band edge (D3). */
  lowerAcceptablePrice: string
  /** "Upper acceptable price" -- printed under the high band edge (D3). */
  upperAcceptablePrice: string
  /** "Typical current spend" -- the reference marker's row label (D8). */
  currentSpend: string
}

export const DEFAULT_PRICE_CURVE_CHART_LABELS: PriceCurveChartLabels = {
  priceSensitivity: 'Price sensitivity',
  optimalPricePoint: 'Optimal price point',
  acceptableRange: 'Acceptable range',
  rangeSeparator: 'to',
  noCurrency:
    'The questionnaire did not record a currency, so these prices are shown unlabelled.',
  lowerAcceptablePrice: 'Lower acceptable price',
  upperAcceptablePrice: 'Upper acceptable price',
  currentSpend: 'Typical current spend',
}

export interface PriceCurveChartProps {
  curve: PriceCurve
  baseLine?: string | null
  labels?: Partial<PriceCurveChartLabels>
}

/**
 * One visual treatment per price series, shared by the plot and the legend.
 *
 * Four distinct styles, not two: the falling pair (`too_cheap`, `cheap`) is
 * dashed, the rising pair (`expensive`, `too_expensive`) is solid, and each
 * pair uses its own chart color so all four lines stay separable without
 * relying on dash alone. The crossings are named after WHICH pair meets, so
 * a legend that collapses two series into one dash pattern reads worse than
 * no legend at all.
 */
const PRICE_SERIES_STYLE: Record<PriceRole, { dash?: string; stroke: string }> = {
  too_cheap: { dash: '1 3', stroke: DECK_CHART_SERIES_COLORS[2] },
  cheap: { dash: '6 3', stroke: DECK_CHART_SERIES_COLORS[2] },
  expensive: { dash: '6 3', stroke: DECK_CHART_SERIES_COLORS[0] },
  too_expensive: { stroke: DECK_CHART_SERIES_COLORS[0] },
}

const PRICE_STYLE_FALLBACK = { stroke: DECK_CHART_SERIES_COLORS[0] }

/**
 * Width reserved for the 0/50/100% axis column, shared by the chart row and
 * the endpoint-label row below it so both line up on the same x origin.
 */
const Y_AXIS_COLUMN_WIDTH = '2.25rem'

/**
 * Decimal places for a displayed number, magnitude-scaled rather than fixed.
 *
 * Mirrors `magnitudeDigits` in `ContinuousDensitySlide.tsx` (the deck's own
 * currency formatter) -- picked deliberately (Step 2) because the same
 * reference median was rendering three different ways across the app (a raw
 * `257.84`, a deck "EUR 258", a written "EUR 257.8"). Kept locale-aware
 * (`toLocaleString(undefined, …)`) rather than the deck's hardcoded `en-US`:
 * this card renders in the live, i18n'd report UI, not a static exported image.
 *
 * TWO FIGURES ARE NO LONGER ITS BUSINESS. The reported corridor rounds through
 * `priceDecimals`, because its precision is the producer's `nice_step`; the
 * reference spend rounds through `referencePriceDecimals`, because its
 * precision is the reference question's own configured display spec. This scale
 * governs what is left (the axis bounds) and stands as the fallback for a
 * stored curve that carries neither. Picking one rule for figures with three
 * different owners is what produced the three spellings in the first place.
 */
function magnitudeDigits(value: number): number {
  const abs = Math.abs(value)
  if (abs >= 100) return 0
  if (abs >= 10) return 1
  return 2
}

export function PriceCurveChart(props: PriceCurveChartProps) {
  const { curve, baseLine, labels: labelsProp } = props
  const labels: PriceCurveChartLabels = { ...DEFAULT_PRICE_CURVE_CHART_LABELS, ...labelsProp }

  const all = curve.series.flatMap((s) => s.points)
  if (all.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground min-h-48"
        data-testid="price-curve-chart"
      >
        No price sensitivity data available
      </div>
    )
  }

  const pointsLo = Math.min(...all.map((p) => p.price))
  const pointsHi = Math.max(...all.map((p) => p.price))
  // The drawing domain is the producer's own cropped, nice window when one is
  // stamped (D1 review 2 #6): a curve can carry more or fewer than the old
  // fixed 60 steps, and an extreme answer must not starve the visible zone.
  // A legacy curve (no `displayWindow`) falls back to the observed point
  // range exactly as before.
  const lo = curve.displayWindow?.low ?? pointsLo
  const hi = curve.displayWindow?.high ?? pointsHi
  const span = hi - lo || 1
  const x = (price: number) => ((price - lo) / span) * 100
  // Grouped thousands and a currency, when the questionnaire captured one --
  // an optimal price of 12500 rendered as "12500" makes a reader count
  // digits, and this card's whole purpose is to say what a business should
  // charge, so an unlabelled "32" is a worse failure than a slow read.
  const currency = curve.currencyCode ?? null
  // `decimals` overrides the magnitude scale for figures whose precision is
  // decided elsewhere and must match it. Only the REPORTED corridor passes one:
  // see `reportedDecimals` below.
  const fmt = (value: number, decimals?: number) => {
    const digits = decimals ?? (Number.isInteger(value) ? 0 : magnitudeDigits(value))
    return value.toLocaleString(undefined, {
      ...(currency ? { style: 'currency', currency } : {}),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  }

  const reported = curve.reported ?? null
  const hasReported = reported != null
  // The reported corridor is ONE figure printed on three surfaces, so all three
  // round it the same way: `PriceSensitivitySlide` and `price_curve_evidence`
  // print `reported.low = 107.5` as "$107.50" and "107.5", while this card's
  // magnitude scale drops to zero decimals at or above 100 and printed "$108".
  // `priceDecimals` is the deck's rule and the single definition of it; the rest
  // of this card's figures are its own and keep the magnitude scale.
  const reportedDecimals = priceDecimals([reported?.low, reported?.high, reported?.point])
  // The reference spend is the exception, and the note above used to say it was
  // "deliberately 258 rather than 257.8". It is not deliberate any more (D8b):
  // that is one figure spelled three ways across the deck slide, this card and
  // the writer's evidence, and the tie is broken by the reference question's own
  // configured display precision. The magnitude scale stays as the fallback for
  // a stored curve that predates the field, so an old card renders unchanged.
  const referenceDecimals = curve.reference
    ? referencePriceDecimals(
        curve.reference.displayDecimals,
        Number.isInteger(curve.reference.value) ? 0 : magnitudeDigits(curve.reference.value),
      )
    : 0
  // D5: below the thin-base line the point estimate is withheld everywhere at
  // once -- no marker, no tile value, no number in any copy -- never merely
  // hidden by CSS while still present in the DOM. The PRODUCER decides it
  // (`build_price_curve` publishes `reported.point = null`); this card reads
  // that typed decision and keeps no thin-base rule of its own.
  // `isThinBase` explains the absence; it is not a gate.
  const showPoint = hasReported && reported!.point != null
  // D8's currency guard runs at the producer (`_guarded_reference` in
  // `price_sensitivity.py`): `curve.reference` only exists when the reference
  // question and the curve named the same currency code. This is the reader
  // boundary's schema guard, retained on purpose: a stale
  // snapshot that carries a reference but lost its own currency code must not
  // draw a price on an axis with no unit.
  const showReference = curve.reference != null && currency != null

  return (
    <div
      data-testid="price-curve-chart"
      className="w-full flex flex-col gap-3"
    >
      <span className="text-sm font-medium text-muted-foreground">{labels.priceSensitivity}</span>
      {hasReported ? (
        <>
          <div>
            <div
              data-testid="price-curve-chart-range"
              className="text-3xl font-bold text-foreground tabular-nums leading-none"
            >
              {fmt(reported!.low, reportedDecimals)} {labels.rangeSeparator} {fmt(reported!.high, reportedDecimals)}
            </div>
            <span className="text-xs text-muted-foreground mt-1 block">{labels.acceptableRange}</span>
          </div>
          {showPoint && (
            <div
              data-testid="price-curve-chart-optimal-row"
              className="flex items-center justify-between text-sm text-foreground tabular-nums"
            >
              <span className="text-muted-foreground">{labels.optimalPricePoint}</span>
              <span data-testid="price-curve-chart-optimal" className="font-semibold">
                {fmt(reported!.point as number, reportedDecimals)}
              </span>
            </div>
          )}
          {showReference && (
            <div
              data-testid="price-curve-chart-current-spend"
              className="flex items-center justify-between text-sm text-foreground tabular-nums"
            >
              <span className="text-muted-foreground">{labels.currentSpend}</span>
              <span
                data-testid="price-curve-chart-current-spend-value"
                className="font-semibold text-[hsl(var(--chart-negative))]"
              >
                {fmt(curve.reference!.value, referenceDecimals)}
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <div
              data-testid="price-curve-chart-optimal"
              className="text-3xl font-bold text-foreground tabular-nums leading-none"
            >
              {fmt(curve.optimalPricePoint)}
            </div>
            <span className="text-xs text-muted-foreground mt-1 block">{labels.optimalPricePoint}</span>
          </div>
          <div data-testid="price-curve-chart-range" className="text-sm text-foreground tabular-nums">
            {labels.acceptableRange} {fmt(curve.pointOfMarginalCheapness)} {labels.rangeSeparator}{' '}
            {fmt(curve.pointOfMarginalExpensiveness)}
          </div>
        </>
      )}
      <div className="flex gap-1.5">
        <div
          className="flex flex-col justify-between h-32 shrink-0"
          style={{ width: Y_AXIS_COLUMN_WIDTH }}
        >
          {['100%', '50%', '0%'].map((tick) => (
            <span key={tick} className="text-xs text-muted-foreground tabular-nums leading-none">
              {tick}
            </span>
          ))}
        </div>
        <svg
          viewBox="0 0 100 60"
          preserveAspectRatio="none"
          className="w-full h-32"
          role="img"
          aria-label="Price sensitivity curves"
        >
          {/* 0 / 50 / 100% gridlines: the curves are shares of the base and
              read as an arbitrary squiggle against no scale at all. */}
          {[0, 50, 100].map((pct) => (
            <line
              key={pct}
              x1={0}
              x2={100}
              y1={58 - (pct / 100) * 56}
              y2={58 - (pct / 100) * 56}
              strokeWidth={0.8}
              vectorEffect="non-scaling-stroke"
              stroke="var(--slide-border-soft, hsl(var(--report-chart-grid)))"
            />
          ))}
          {/* The shaded acceptable range and its two edges sit on the EXACT
              crossings (`pointOfMarginalCheapness` / `pointOfMarginalExpensiveness`),
              never on `reported.low` / `reported.high` -- D1, "geometry is
              exact, labels are rounded". */}
          {hasReported && (
            <rect
              data-testid="price-curve-chart-band"
              x={x(curve.pointOfMarginalCheapness)}
              y={0}
              width={Math.max(0, x(curve.pointOfMarginalExpensiveness) - x(curve.pointOfMarginalCheapness))}
              height={60}
              fill="var(--slide-border-soft, hsl(var(--report-chart-grid)))"
              opacity={0.5}
            />
          )}
          {curve.series.map((series) => {
            const style = PRICE_SERIES_STYLE[series.role] ?? PRICE_STYLE_FALLBACK
            return (
              <polyline
                key={series.role}
                fill="none"
                strokeWidth={1.4}
                vectorEffect="non-scaling-stroke"
                strokeDasharray={style.dash}
                stroke={style.stroke}
                points={series.points
                  .map((pt) => `${x(pt.price)},${58 - (pt.share / 100) * 56}`)
                  .join(' ')}
              />
            )
          })}
          {hasReported && (
            <>
              <line
                data-testid="price-curve-chart-band-edge-low"
                x1={x(curve.pointOfMarginalCheapness)}
                x2={x(curve.pointOfMarginalCheapness)}
                y1={0}
                y2={60}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                stroke="var(--slide-text-secondary, currentColor)"
              />
              <line
                data-testid="price-curve-chart-band-edge-high"
                x1={x(curve.pointOfMarginalExpensiveness)}
                x2={x(curve.pointOfMarginalExpensiveness)}
                y1={0}
                y2={60}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
                stroke="var(--slide-text-secondary, currentColor)"
              />
            </>
          )}
          {/* The optimal marker: drawn at the exact `optimalPricePoint`
              regardless of rounding, but only PAINTED when the point estimate
              is published (D5) -- the same test that gates the tile above. */}
          {(!hasReported || showPoint) && (
            <line
              data-testid="price-curve-chart-optimal-marker"
              x1={x(curve.optimalPricePoint)}
              x2={x(curve.optimalPricePoint)}
              y1={0}
              y2={60}
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
              strokeDasharray="3 3"
              stroke="var(--slide-text-primary)"
            />
          )}
          {showReference && (
            <line
              data-testid="price-curve-chart-reference-marker"
              x1={x(curve.reference!.value)}
              x2={x(curve.reference!.value)}
              y1={0}
              y2={60}
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
              strokeDasharray="1 2"
              stroke="hsl(var(--chart-negative))"
            />
          )}
        </svg>
      </div>
      {hasReported && (
        <div className="relative h-7" data-testid="price-curve-chart-endpoints">
          <div className="absolute inset-0 flex" style={{ paddingLeft: Y_AXIS_COLUMN_WIDTH }}>
            <div className="relative flex-1">
              <span
                data-testid="price-curve-chart-endpoint-low"
                className="absolute -translate-x-1/2 text-center text-[10px] leading-tight text-muted-foreground"
                style={{ left: `${x(curve.pointOfMarginalCheapness)}%` }}
              >
                {labels.lowerAcceptablePrice}
                <br />
                <span className="font-semibold text-foreground tabular-nums">{fmt(reported!.low, reportedDecimals)}</span>
              </span>
              <span
                data-testid="price-curve-chart-endpoint-high"
                className="absolute -translate-x-1/2 text-center text-[10px] leading-tight text-muted-foreground"
                style={{ left: `${x(curve.pointOfMarginalExpensiveness)}%` }}
              >
                {labels.upperAcceptablePrice}
                <br />
                <span className="font-semibold text-foreground tabular-nums">{fmt(reported!.high, reportedDecimals)}</span>
              </span>
            </div>
          </div>
        </div>
      )}
      {/* The drawn domain's two ends. Identified individually so a test can
          read either bound on its own: asserted against the whole card's
          textContent, a low bound of "10" is satisfied by the "110" the range
          tile always prints, and the claim disappears. */}
      <div data-testid="price-curve-chart-axis-bounds" className="flex justify-between -mt-1">
        <span
          data-testid="price-curve-chart-axis-low"
          className="text-xs text-muted-foreground tabular-nums"
        >
          {fmt(lo)}
        </span>
        <span
          data-testid="price-curve-chart-axis-high"
          className="text-xs text-muted-foreground tabular-nums"
        >
          {fmt(hi)}
        </span>
      </div>
      <div data-testid="price-curve-chart-legend" className="flex flex-wrap gap-x-3 gap-y-1">
        {curve.series.map((series) => {
          const style = PRICE_SERIES_STYLE[series.role] ?? PRICE_STYLE_FALLBACK
          return (
            <span key={series.role} className="flex items-center gap-1.5">
              <svg width="16" height="6" aria-hidden="true" className="shrink-0">
                <line
                  x1="0"
                  y1="3"
                  x2="16"
                  y2="3"
                  strokeWidth={1.6}
                  strokeDasharray={style.dash}
                  stroke={style.stroke}
                />
              </svg>
              <span className="text-xs text-muted-foreground">{series.label}</span>
            </span>
          )
        })}
      </div>
      {currency === null && (
        <span
          data-testid="price-curve-chart-no-currency"
          className="text-xs text-muted-foreground leading-snug"
        >
          {labels.noCurrency}
        </span>
      )}
      {baseLine && (
        <span className="text-xs text-muted-foreground tabular-nums">{baseLine}</span>
      )}
    </div>
  )
}
